/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { get as _get, has as _has, set as _set } from 'lodash';
import { IAny, IDictionary } from '../../interfaces';
import { IRenderingHelper } from '../../interfaces/IRenderingHelper';
import dragAndDropImage from './drag-and-drop-image';
import dragAndDropPlaceholder from './drag-and-drop-placehoder';
import {
    cleanDataInfo,
    ElementDataInfo,
    EMPTY_SLOT_TYPE_NAME,
    getElementById,
    getElementDataInfoById,
    getElementDataInfoByTarget,
    getModuleById,
    getRenderingHelper,
    updateAddModuleInnerContainerClass
} from './drag-and-drop-utils';

/**
 * Provides the drag and drop events implementation
 */
export class DragAndDropEvents {
    private cache: IDictionary<IAny>;
    private dragElement: ElementDataInfo;
    private dropElement: ElementDataInfo;
    private isCursorOnDroppableArea: boolean;
    private isDragging: boolean;
    private renderingHelper: IRenderingHelper | undefined;
    private rootModuleId: string | undefined;

    constructor() {
        this.cache = {};
        this.dragElement = cleanDataInfo();
        this.dropElement = cleanDataInfo();
        this.isCursorOnDroppableArea = false;
        this.isDragging = false;
    }

    /**
     * Set draggable events for a specific module.
     * @param sourceModuleId The module to be set as draggable.
     * @param rootModuleId The root module to be set as droppable.
     */
    public setModuleAsDraggable = (sourceModuleId: string, rootModuleId: string): void => {
        this.renderingHelper = getRenderingHelper();
        if (this.renderingHelper) {
            dragAndDropPlaceholder.setRenderingHelper(this.renderingHelper);
        }
        this._setRootModuleId(rootModuleId);
        this._setRootModuleAsDroppable();

        const module = getModuleById(sourceModuleId);
        if (module) {
            module.addEventListener('dragstart', this._onDragStart);
            module.addEventListener('drag', this._onDrag);
            module.addEventListener('dragend', this._onDragEnd);
            module.setAttribute('draggable', 'true');
        }
    };

    /**
     * Set the root module Id.
     * @param moduleId The root module Id.
     */
    private _setRootModuleId = (moduleId: string): void => {
        this.rootModuleId = moduleId;
    };

    /**
     * Clear cache object.
     */
    private _clearCache = (): void => {
        this.cache = {};
    };

    /**
     * Check if cache has the information whether or not a module can be dropped in parent container.
     * @param moduleId The module id.
     * @param parentId The parent container id.
     * @param slotId The slot id.
     * @returns {boolean} Returns true cache has the information whether or not a module can be dropped in parent container. Otherwise returns false.
     */
    private _cacheHasIsDroppableInfo = (moduleId: string, parentId: string, slotId: string): boolean => {
        return _has(this.cache, this._getCacheKey(moduleId, parentId, slotId));
    };

    /**
     * Return whether or not a module can be dropped in parent container.
     * @param moduleId The module id.
     * @param parentId The parent container id.
     * @param slotId The slot id.
     * @returns {boolean | unknown} Returns whether or not a module can be dropped in parent container.
     */
    private _isModuleDroppable = (moduleId: string, parentId: string, slotId: string): boolean | unknown => {
        if (!parentId || !moduleId || !slotId) {
            return false;
        }
        this._checkIsDroppableAndAddToCache(moduleId, parentId, slotId);
        return _get(this.cache, this._getCacheKey(moduleId, parentId, slotId), false);
    };

    /**
     * Set whether or not a module can be dropped in parent container.
     * @param moduleId The module id.
     * @param parentId The parent container id.
     * @param slotId The slot id.
     */
    private _setModuleIsDroppable = (moduleId: string, parentId: string, slotId: string, canBeDrop: boolean): void => {
        _set(this.cache, this._getCacheKey(moduleId, parentId, slotId), canBeDrop);
    };

    /**
     * Create a key to cache information whether or not a module is droppable in parent container.
     * @param moduleId The module id.
     * @param parentId The parent container id.
     * @param slotId The slot id.
     * @returns {string} Returns the cache key.
     */
    private _getCacheKey = (moduleId: string, parentId: string, slotId: string): string => {
        return `${moduleId}_${parentId}_${slotId}`;
    };

    /**
     * Check if module can be dropped in parent container and cache the information.
     * @param moduleId The module id.
     * @param parentId The parent container id.
     * @param slotId The slot id.
     */
    private _checkIsDroppableAndAddToCache = (moduleId: string, parentId: string, slotId: string): void => {
        if (!this._cacheHasIsDroppableInfo(moduleId, parentId, slotId)) {
            const elementDataInfo = getElementDataInfoById(parentId);
            if (elementDataInfo.dataInfo.isFragment || elementDataInfo.dataInfo.isFragmentModule) {
                this._setModuleIsDroppable(moduleId, parentId, slotId, false);
            } else {
                const isDroppable = this.renderingHelper?.isDroppable(moduleId, parentId, slotId) || false;
                this._setModuleIsDroppable(moduleId, parentId, slotId, isDroppable);
            }
        }
    };

    /**
     * Handles the on drag over event for containers.
     * @param e The react drag event.
     */
    private _onDragOver = (e: Event): void => {
        if (!this.isDragging) {
            return;
        }

        e.preventDefault();
        this.isCursorOnDroppableArea = true;
        const dragEvent = <DragEvent>e;
        const elementDataInfo = getElementDataInfoByTarget(<HTMLElement>e.target);
        if (elementDataInfo.dataInfo.typeName === EMPTY_SLOT_TYPE_NAME) {
            dragAndDropImage.updatePosition(dragEvent.pageX, dragEvent.pageY);
            return;
        }

        this.dropElement = elementDataInfo;
        if (this._isModuleDroppable(this.dragElement.dataInfo.id, this.dropElement.dataInfo.parentId, this.dropElement.dataInfo.slotId)) {
            dragAndDropPlaceholder.setPlaceHolderOnPosition(this.dragElement, this.dropElement, dragEvent.clientY);
            if (dragAndDropImage.isStatusNotDroppable()) {
                dragAndDropImage.setDropAllowedImage(this.dragElement, dragEvent.pageX, dragEvent.pageY);
            }
        } else if (dragAndDropImage.isStatusDroppable()) {
            dragAndDropPlaceholder.clearAll();
            dragAndDropImage.setDropNotAllowedImage(dragEvent.pageX, dragEvent.pageY);
        }
        dragAndDropImage.updatePosition(dragEvent.pageX, dragEvent.pageY);
        this.isCursorOnDroppableArea = false;
    };

    /**
     * Handles the on drag enter event for containers.
     * @param e The react drag event.
     */
    private _onDragEnter = (e: Event): void => {
        if (this.isDragging) {
            e.preventDefault();
            this.isCursorOnDroppableArea = true;
            const elementDataInfo = getElementDataInfoByTarget(<HTMLElement>e.target);
            if (
                elementDataInfo.dataInfo.typeName === EMPTY_SLOT_TYPE_NAME &&
                elementDataInfo.element &&
                elementDataInfo.element.firstElementChild
            ) {
                const dragEvent = <DragEvent>e;
                // Remove placeholder from canvas and from site builder.
                dragAndDropPlaceholder.clearAll();

                if (this._isModuleDroppable(this.dragElement.dataInfo.id, elementDataInfo.dataInfo.id, elementDataInfo.dataInfo.slotId)) {
                    this.dropElement = elementDataInfo;
                    // Get the container inner element and update its class.
                    updateAddModuleInnerContainerClass(
                        elementDataInfo.element.firstElementChild,
                        this.dragElement.dataInfo.isFragment ? 'InsertFragment' : 'InsertModule'
                    );
                    // Add placeholder to position 0 on site builder.
                    this.renderingHelper?.onDrag(elementDataInfo.dataInfo.id, 0, elementDataInfo.dataInfo.slotId);

                    dragAndDropImage.setDropAllowedImage(this.dragElement, dragEvent.pageX, dragEvent.pageY);
                } else {
                    // Get the container inner element and update its class.
                    updateAddModuleInnerContainerClass(elementDataInfo.element.firstElementChild, 'MoveNotAllowed');
                    if (dragAndDropImage.isStatusDroppable()) {
                        dragAndDropImage.setDropNotAllowedImage(dragEvent.pageX, dragEvent.pageY);
                    }
                }
            }
        }
    };

    /**
     * Handles the on drag leave event for containers.
     * @param e The react drag event.
     */
    private _onDragLeave = (e: Event): void => {
        if (this.isDragging) {
            e.preventDefault();
            const elementDataInfo = getElementDataInfoByTarget(<HTMLElement>e.target);
            if (
                elementDataInfo.dataInfo.typeName === EMPTY_SLOT_TYPE_NAME &&
                elementDataInfo.element &&
                elementDataInfo.element.firstElementChild
            ) {
                updateAddModuleInnerContainerClass(elementDataInfo.element.firstElementChild);
            }
            this.isCursorOnDroppableArea = false;
        }
    };

    /**
     * Handles the on drop event for containers.
     * @param e The react drag event.
     */
    private _onDrop = (e: Event): void => {
        if (this.isDragging) {
            e.preventDefault();
            if (this.dropElement.dataInfo.typeName === EMPTY_SLOT_TYPE_NAME) {
                if (this._isModuleDroppable(this.dragElement.dataInfo.id, this.dropElement.dataInfo.id, this.dropElement.dataInfo.slotId)) {
                    this.renderingHelper?.onDrop(
                        this.dragElement.dataInfo.id,
                        this.dropElement.dataInfo.id,
                        0,
                        this.dropElement.dataInfo.slotId
                    );
                }
                if (this.dropElement.element && this.dropElement.element.firstElementChild) {
                    updateAddModuleInnerContainerClass(this.dropElement.element.firstElementChild);
                }
            } else {
                const placeholderInfo = dragAndDropPlaceholder.getPlaceholder();
                if (placeholderInfo) {
                    const { moduleId, parentId, index, slotId } = placeholderInfo;
                    if (index >= 0 && this._isModuleDroppable(moduleId, parentId, slotId)) {
                        this.renderingHelper?.onDrop(moduleId, parentId, index, slotId);
                    }
                }
            }
        }
    };

    /**
     * Remove draggable attributes and events.
     * @param moduleId The module to have attributes removed.
     */
    private _removeDraggableEvents = (moduleId: string): void => {
        if (moduleId) {
            const draggableModule = getModuleById(moduleId);
            if (draggableModule) {
                draggableModule.removeEventListener('dragstart', this._onDragStart);
                draggableModule.removeEventListener('drag', this._onDrag);
                draggableModule.removeEventListener('dragend', this._onDragEnd);
                draggableModule.removeAttribute('draggable');
            }
        }
    };

    /**
     * Handles the drag event.
     * @param e The drag event.
     */
    private _onDrag = (e: Event): void => {
        if (!this.isDragging || !this.isCursorOnDroppableArea) {
            dragAndDropImage.hide();
        }
    };

    /**
     * Handles the drag start event.
     * @param e The drag event.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _onDragStart = (e: Event): void => {
        const dragEvent = <DragEvent>e;
        this.isDragging = true;
        this.isCursorOnDroppableArea = true;
        this.dragElement = getElementDataInfoByTarget(<HTMLElement>e.target);
        dragAndDropImage.setDropAllowedImage(this.dragElement, dragEvent.pageX, dragEvent.pageY);
        // Set drag image to a empty div.
        // This prevents the ghost image from being displayed.
        dragEvent.dataTransfer?.setDragImage(dragAndDropImage.setTransparentImage(), 0, 0);
    };

    /**
     * Handles the drag end event.
     * @param e The drag event.
     */
    private _onDragEnd = (e: Event): void => {
        this.isDragging = false;
        this.isCursorOnDroppableArea = false;
        // remove draggable events
        this._removeDraggableEvents(this.dragElement.dataInfo.id);
        this._removeDroppableEvents();
        this._clearAllCache();
    };

    /**
     * Clear all cache information.
     */
    private _clearAllCache = (): void => {
        this.dragElement = cleanDataInfo();
        this.dropElement = cleanDataInfo();
        this.isDragging = false;
        this.isCursorOnDroppableArea = false;
        this._clearCache();
        dragAndDropPlaceholder.clearAll();
        dragAndDropImage.clear();
    };

    /**
     * Set droppable events for the root module.
     */
    private _setRootModuleAsDroppable = (): void => {
        const droppableModule = getElementById(this.rootModuleId || '');
        if (droppableModule) {
            droppableModule.addEventListener('dragenter', this._onDragEnter);
            droppableModule.addEventListener('dragleave', this._onDragLeave);
            droppableModule.addEventListener('dragover', this._onDragOver);
            droppableModule.addEventListener('drop', this._onDrop);
        }
    };

    /**
     * Remove droppable events.
     */
    private _removeDroppableEvents = (): void => {
        const droppableModule = getElementById(this.rootModuleId || '');
        if (droppableModule) {
            droppableModule.removeEventListener('dragenter', this._onDragEnter);
            droppableModule.removeEventListener('dragleave', this._onDragLeave);
            droppableModule.removeEventListener('dragover', this._onDragOver);
            droppableModule.removeEventListener('drop', this._onDrop);
        }
    };
}
const dragAndDropEvents = new DragAndDropEvents();
export default dragAndDropEvents;
